import { useEffect, useState } from "react";
import { logger } from "../lib/logger";
import { useLang } from "../contexts/LangContext";
import Header from "../components/site/Header";
import { placeholder } from "../lib/constants";
import Hero from "../components/site/Hero";
import ServicesHub from "../components/site/ServicesHub";
import About from "../components/site/About";
import Portfolio from "../components/site/Portfolio";
import Testimonials from "../components/site/Testimonials";
import Faq from "../components/site/Faq";
import Blog from "../components/site/Blog";
import Contact from "../components/site/Contact";
import Footer from "../components/site/Footer";

function applySiteData(d, setters) {
  setters.setWhatsapp(d.globals?.whatsapp_number || "");
  setters.setPortfolioItems((d.portfolio || []).filter(p => p.published === 1 || p.published === true));
  setters.setTestimonialItems(d.testimonials || []);
  setters.setBlogItems((d.blog_posts || []).filter(p => p.published === 1 || p.published === true));
  setters.setHeroContent(d.hero);
  setters.setAboutContent(d.about);
  setters.setGlobals(d.globals);
  setters.setSectionHeaders(d.section_headers || d.sectionHeaders);
  setters.setNavigation(d.navigation);
  setters.setFormServices(d.form_services || d.formServices);
  setters.setContactMethods(d.contact_methods || d.contactMethods);
  setters.setFormPrefixes(d.form_prefixes || d.formPrefixes);
  setters.setFormNationalities(d.form_nationalities || d.formNationalities);
  setters.setUiStrings(d.ui_strings || d.uiStrings);

  const manualFaqs = d.faqs || [];
  const publishedQuestions = (d.faq_questions || d.faqQuestions || [])
    .filter(q => q.status === "published" && q.answer)
    .map(q => ({ id: q.uid, question: q.question, answer: q.answer, sort_order: 99 }));
  setters.setFaqItems([...manualFaqs, ...publishedQuestions]);
  setters.setFaqCategories(d.faq_categories || d.faqCategories || []);

  const s = d.settings || {};
  setters.setEnabledSections({
    hero: s.section_hero_visible !== "false",
    about: s.section_about_visible !== "false",
    services: s.section_services_visible !== "false",
    portfolio: s.section_portfolio_visible !== "false",
    testimonials: s.section_testimonials_visible !== "false",
    faq: s.section_faq_visible !== "false",
    blog: s.section_blog_visible !== "false",
    contact: s.section_contact_visible !== "false",
  });

  setters.setElementVisibility({
    hero_cta: s.element_hero_cta_visible !== "false",
    hero_scroll: s.element_hero_scroll_visible !== "false",
    about_image: s.element_about_image_visible !== "false",
    about_stats: s.element_about_stats_visible !== "false",
    services_details: s.element_services_details_visible !== "false",
    services_carousel: s.element_services_carousel_visible !== "false",
    contact_info: s.element_contact_info_visible !== "false",
    contact_form: s.element_contact_form_visible !== "false",
    faq_form: s.element_faq_form_visible !== "false",
    faq_items: s.element_faq_items_visible !== "false",
  });

  const ogTitle = s.og_title || placeholder("short");
  const ogDesc = s.og_description || placeholder("medium");
  const ogImg = s.og_image || "";
  const ogUrl = s.og_url || "";
  function setMeta(prop, content) {
    let el = document.querySelector(`meta[property="${prop}"]`);
    if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
    el.setAttribute("content", content);
  }
  setMeta("og:title", ogTitle);
  setMeta("og:description", ogDesc);
  setMeta("og:image", ogImg);
  setMeta("og:url", ogUrl);

  const displayServices = (d.services || [])
    .filter(s => s.published === true || s.published === 1)
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  setters.setServicesData(displayServices);
}

const DEFAULT_LOADER = {
  bgClass: "bg-offwhite",
  textClass: "font-serif text-2xl animate-pulse text-softblack",
  accentClass: "text-champagne",
  text: "Hub",
  accent: ".",
};

export default function Landing() {
  const { lang } = useLang();
  const [loading, setLoading] = useState(true);
  const [loaderConfig, setLoaderConfig] = useState(DEFAULT_LOADER);
  const [whatsapp, setWhatsapp] = useState("");
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [testimonialItems, setTestimonialItems] = useState([]);
  const [blogItems, setBlogItems] = useState([]);
  const [heroContent, setHeroContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [servicesData, setServicesData] = useState([]);
  const [globals, setGlobals] = useState(null);
  const [sectionHeaders, setSectionHeaders] = useState(null);
  const [navigation, setNavigation] = useState(null);
  const [formServices, setFormServices] = useState(null);
  const [contactMethods, setContactMethods] = useState(null);
  const [formPrefixes, setFormPrefixes] = useState([]);
  const [formNationalities, setFormNationalities] = useState([]);
  const [uiStrings, setUiStrings] = useState(null);
  const [faqItems, setFaqItems] = useState([]);
  const [faqCategories, setFaqCategories] = useState([]);
  const [enabledSections, setEnabledSections] = useState(null);
  const [elementVisibility, setElementVisibility] = useState(null);

  function applyLoaderConfig(data) {
    const s = data.settings || {};
    const g = data.globals || {};
    const text = s.loader_text || g.logo?.split(" ")[0] || DEFAULT_LOADER.text;
    setLoaderConfig({
      bgClass: s.loader_bg_class || DEFAULT_LOADER.bgClass,
      textClass: s.loader_text_class || DEFAULT_LOADER.textClass,
      accentClass: s.loader_accent_class || DEFAULT_LOADER.accentClass,
      text: text,
      accent: s.loader_accent !== undefined ? s.loader_accent : DEFAULT_LOADER.accent,
    });
  }

  useEffect(() => {
    async function loadAllData() {
      try {
        const loaderPromise = fetch(`/api/loader-config?locale=${lang}`)
          .then(async r => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); } return r.json(); })
          .then(data => { if (data) applyLoaderConfig(data); })
          .catch(err => logger.error("Error loading loader config:", err));

        const siteRes = await fetch(`/api/site-data?locale=${lang}`);
        if (!siteRes.ok) { const errBody = await siteRes.json().catch(() => ({})); throw new Error(errBody.error || `HTTP ${siteRes.status}`); }
        const siteData = await siteRes.json();
        applySiteData(siteData, { setWhatsapp, setPortfolioItems, setTestimonialItems, setBlogItems, setHeroContent, setAboutContent, setGlobals, setSectionHeaders, setNavigation, setFormServices, setContactMethods, setFormPrefixes, setFormNationalities, setUiStrings, setFaqItems, setFaqCategories, setEnabledSections, setElementVisibility, setServicesData });

        await loaderPromise;
      } catch (err) {
        logger.error("Error loading site data:", err);
      } finally {
        setLoading(false);
      }
    }
    loadAllData();
  }, [lang]);

  useEffect(() => {
    if (loading) return;
    const hash = window.location.hash;
    if (hash) {
      const id = hash.replace("#", "");
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      });
    }
  }, [loading]);

  const navHrefs = !navigation ? [] : navigation
    .filter(item => item.href?.startsWith("#"))
    .map(item => item.href.replace("#", ""));

  if (loading) {
    return (
      <div className={`min-h-screen ${loaderConfig.bgClass} flex items-center justify-center`}>
        <div className={loaderConfig.textClass}>
          {loaderConfig.text}
          <span className={loaderConfig.accentClass}>{loaderConfig.accent}</span>
        </div>
      </div>
    );
  }

  return (
    <div data-id="page-landing" className="App bg-offwhite">
      <Header navigation={navigation} globals={globals} hasServices={servicesData.length > 0} hasPortfolio={portfolioItems.length > 0} hasTestimonials={testimonialItems.length > 0} hasBlog={blogItems.length > 0} enabledSections={enabledSections} uiStrings={uiStrings} contactHref={navHrefs[6] ? `#${navHrefs[6]}` : "#contato"} />
      <main>
        {enabledSections?.hero !== false && <Hero whatsappNumber={whatsapp} content={heroContent} uiStrings={uiStrings} elementVisibility={elementVisibility} servicesHref={navHrefs[0] ? `#${navHrefs[0]}` : "#servicos"} />}
        {enabledSections?.services !== false && servicesData.length > 0 && (
          <ServicesHub 
            services={servicesData} 
            sectionContent={sectionHeaders?.services} 
            uiStrings={uiStrings}
            globals={globals}
            elementVisibility={elementVisibility}
            sectionId={navHrefs[0]}
            contactHref={navHrefs[6] ? `#${navHrefs[6]}` : "#contato"}
          />
        )}
        {enabledSections?.about !== false && <About content={aboutContent} uiStrings={uiStrings?.general} elementVisibility={elementVisibility} sectionId={navHrefs[1]} />}
        {enabledSections?.portfolio !== false && portfolioItems.length > 0 && (
          <Portfolio 
            items={portfolioItems} 
            sectionContent={sectionHeaders?.portfolio}
            uiStrings={uiStrings}
            sectionId={navHrefs[2]}
          />
        )}
        {enabledSections?.testimonials !== false && testimonialItems.length > 0 && (
          <Testimonials items={testimonialItems} sectionContent={sectionHeaders?.testimonials} uiStrings={uiStrings} sectionId={navHrefs[3]} />
        )}
        {enabledSections?.faq !== false && <Faq items={faqItems} sectionContent={sectionHeaders?.faq} elementVisibility={elementVisibility} faqCategories={faqCategories} uiStrings={uiStrings} sectionId={navHrefs[4]} />}
        {enabledSections?.blog !== false && blogItems.length > 0 && (
          <Blog
            items={blogItems}
            sectionContent={sectionHeaders?.blog}
            uiStrings={uiStrings}
            sectionId={navHrefs[5]}
          />
        )}
        {enabledSections?.contact !== false && (
          <Contact 
            whatsappNumber={whatsapp} 
            sectionContent={sectionHeaders?.contact} 
            formServices={formServices}
            contactMethods={contactMethods}
            formPrefixes={formPrefixes}
            globals={globals}
            uiStrings={uiStrings}
            elementVisibility={elementVisibility}
            sectionId={navHrefs[6]}
          />
        )}
      </main>
        <Footer globals={globals} whatsappNumber={whatsapp} navigation={navigation} uiStrings={uiStrings} enabledSections={enabledSections} hasServices={servicesData.length > 0} hasPortfolio={portfolioItems.length > 0} hasTestimonials={testimonialItems.length > 0} hasBlog={blogItems.length > 0} partnerLogos={aboutContent?.partner_logos || []} />
    </div>
  );
}
